/* eslint-disable @angular-eslint/prefer-inject */
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AbstractDataService, LoggerService } from '@pf/shared-services';
import {
    ZoneCustomFieldsService,
    ZonesService,
} from '@control-tower/platform-wms';
import {
    EntityChangeTrackingDto,
    IChangesSearchParams,
    ICustomField,
    IPagedResult,
    PFWmsEntities,
} from '@pf/shared-common';
import { ZoneStore } from './zone.store';
import {
    ZoneCreate,
    ZoneRead,
    ZoneSearchParams,
} from '../../entities/zone.dto';
import { Operation } from 'fast-json-patch';

@Injectable()
export class ZoneDataService extends AbstractDataService<ZoneRead, ZoneCreate> {
    private customFieldsService = inject(ZoneCustomFieldsService);

    protected EntityName = PFWmsEntities.Zone;
    constructor(
        private service: ZonesService,
        store: ZoneStore,
        logger: LoggerService
    ) {
        super(store, logger);
    }
    protected createObs$(body: ZoneCreate): Observable<ZoneRead> {
        return this.service.zoneCreate({
            zoneViewModelCreateBody: body,
        }) as Observable<ZoneRead>;
    }
    protected deleteObs$(id: string): Observable<ZoneRead> {
        return this.service.zoneDelete({
            id,
        }) as Observable<ZoneRead>;
    }
    protected undoDeleteObs$(id: string): Observable<ZoneRead> {
        return this.service.zoneUndoDelete({
            id,
        }) as Observable<ZoneRead>;
    }
    protected getObs$(id: string): Observable<ZoneRead> {
        return this.service.zoneReadById({
            id,
        }) as Observable<ZoneRead>;
    }
    protected updateObs$(
        id: string,
        operations: Operation[]
    ): Observable<ZoneRead> {
        return this.service.zoneUpdatePatch({
            id,
            operation: operations,
        }) as Observable<ZoneRead>;
    }
    protected searchObs$(
        searchParams: ZoneSearchParams
    ): Observable<IPagedResult<ZoneRead>> {
        return this.service.zoneSearch(searchParams) as Observable<
            IPagedResult<ZoneRead>
        >;
    }

    protected searchChangesObs$(
        searchParams: IChangesSearchParams
    ): Observable<IPagedResult<EntityChangeTrackingDto<ZoneRead>>> {
        return this.service.zoneGetChanges(searchParams) as Observable<
            IPagedResult<EntityChangeTrackingDto<ZoneRead>>
        >;
    }

    protected override createCustomFieldObs$(
        entityId: string,
        customField: ICustomField
    ): Observable<ICustomField & { id: string }> {
        return this.customFieldsService.zoneCreateCustomField({
            entityId,
            customFieldViewModelCreate: {
                key: customField.key,
                value: customField.value,
            },
        }) as Observable<ICustomField & { id: string }>;
    }

    protected override updateCustomFieldObs$(
        entityId: string,
        customFieldId: string,
        customField: ICustomField
    ): Observable<ICustomField & { id: string }> {
        return this.customFieldsService.zoneCustomFieldsUpdatePut({
            entityId,
            id: customFieldId,
            customFieldViewModelCreate: {
                key: customField.key,
                value: customField.value,
            },
        }) as Observable<ICustomField & { id: string }>;
    }

    protected override deleteCustomFieldObs$(
        entityId: string,
        customFieldId: string
    ): Observable<ICustomField & { id: string }> {
        return this.customFieldsService.zoneDeleteCustomField({
            entityId,
            id: customFieldId,
        }) as Observable<ICustomField & { id: string }>;
    }
}
